import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Comment } from 'src/app/models/comment.model';
import { CommentService } from 'src/app/services/comment.service';
import { ColumnSpan } from 'src/app/shared/form/columnSpan.model';
import { FormComponent, IFormDialogData } from 'src/app/shared/form/form.component';
import { DateTimeFormField, TextAreaFormField } from 'src/app/shared/form/formField.model';
import nameOf from 'src/app/shared/form/nameOf.helper';

export interface ICommentDialogData extends IFormDialogData {
  text: string;
  fromDate: number | Date | string;
  toDate: number | Date | string;
  endPointId: string;
  dateCreated: number;
}

@Component({
  standalone: false,
  selector: 'app-comments-dialog',
  templateUrl: './../../shared/form/form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./../../shared/form/form.component.scss', './comments-dialog.component.scss'],
})

export class CommentsDialogComponent extends FormComponent<ICommentDialogData> implements OnInit {
  constructor(
    dialogRef: MatDialogRef<CommentsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ICommentDialogData,
    commentService: CommentService,
    protected injector: Injector
  ) {
    super(dialogRef, commentService, data, 'Comment', injector);
  }

  ngOnInit(): void {
    this.getEntity().subscribe(entity=> {
      this.entity = entity;
      this.initModel();
      this.initFields();
    })
  }

  initModel(): void {
    const fromDate = this.normalizeDateValue(this.data.fromDate);
    const toDate = this.normalizeDateValue(this.data.toDate);

    if (fromDate) {
      this.entity.fromDate = fromDate;
    }

    if (toDate) {
      this.entity.toDate = toDate;
    }
  }

  initFields(): void {
    this.fields = [
      new DateTimeFormField({
        property: nameOf<Comment>(x => x.fromDate),
        name: this.translateService.getDynamicTranslation('From Date'),
        columnSpan: new ColumnSpan(1, 2),
      }),
      new DateTimeFormField({
        property: nameOf<Comment>(x => x.toDate),
        name: this.translateService.getDynamicTranslation('To Date'),
        columnSpan: new ColumnSpan(1, 2),
      }),
      new TextAreaFormField({
        property: nameOf<Comment>(x => x.text),
        name: this.translateService.getDynamicTranslation('Comment'),
        columnSpan: new ColumnSpan(1, 1),
      }),
    ]
  }

  /**
   * Populate the From/To Date fields from a graph-picked window (timestamps in ms).
   * Lets the user keep selecting a start/stop window on the graph while this dialog stays open.
   */
  setDateRange(fromTimestampMs: number, toTimestampMs: number): void {
    const startMs = Math.min(fromTimestampMs, toTimestampMs);
    const endMs = Math.max(fromTimestampMs, toTimestampMs);
    const fromDate = this.dateFormatService.convertTimestampToDate(Math.floor(startMs / 1000));
    const toDate = this.dateFormatService.convertTimestampToDate(Math.floor(endMs / 1000));

    if (fromDate) {
      this.entity.fromDate = fromDate;
    }
    if (toDate) {
      this.entity.toDate = toDate;
    }
    this.updateFieldVisibility();
  }

  onBeforeSave(model: ICommentDialogData): void {
    model.endPointId = this.data.endPointId;
    model.fromDate = this.dateFormatService.convertDateToTimestamp(this.normalizeDateValue(model.fromDate));
    model.toDate = this.dateFormatService.convertDateToTimestamp(this.normalizeDateValue(model.toDate));
  }

  private normalizeDateValue(value: number | Date | string): Date | undefined {
    if (value instanceof Date) {
      return value;
    }

    if (typeof value === 'number') {
      return this.dateFormatService.convertTimestampToDate(value);
    }

    if (typeof value === 'string') {
      return this.dateFormatService.convertStringToDate(value);
    }

    return undefined;
  }

  getNewObject(): Comment {
    return new Comment();
  }

  validate(): boolean {
    const fromDate = this.entity.fromDate as Date | undefined;
    const toDate = this.entity.toDate as Date | undefined;

    if (!fromDate || !toDate) {
      return true;
    }

    if (toDate < fromDate) {
      this.toastService.displayErrorMessage('End Date cannot be before Start Date', true);
      return false;
    }
    return true;
  }
}
